// [Codex 2026-07-12] S135 keeps VIX severity on the value while the compact shell stays neutral black glass.
import { useEffect, useRef, useState } from "react";
import type { IVScoreResponse } from "../../types/market-data";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface HeaderVixChipProps {
  data: IVScoreResponse | null;
  compact?: boolean;
}

type VixPulseTone = "bullish" | "bearish" | null;

export function HeaderVixChip({ data, compact = false }: HeaderVixChipProps) {
  const level = data?.vix.level ?? null;
  const previousLevelRef = useRef<number | null>(null);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pulseTone, setPulseTone] = useState<VixPulseTone>(null);

  useEffect(() => {
    if (level == null) return;
    const rounded = Number(level.toFixed(2));
    const previous = previousLevelRef.current;
    previousLevelRef.current = rounded;
    if (previous == null || previous === rounded) return;

    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    setPulseTone(rounded < previous ? "bullish" : "bearish");
    pulseTimerRef.current = setTimeout(() => setPulseTone(null), 1100);
  }, [level]);

  useEffect(
    () => () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    },
    [],
  );

  return (
    <div
      className={`fintheon-header-data-chip fintheon-header-vix-chip fintheon-black-liquid-glass fintheon-glass-pill header-widget-glass solvys-liquid-glass h-7 rounded-lg px-2.5 flex items-center ${
        pulseTone ? `fintheon-header-vix-chip--${pulseTone}` : ""
      }`}
      data-vix-regime={vixRegime(level)}
    >
      <div className="flex items-center gap-1.5">
        {!compact && <span className="text-[9px] text-gray-500">VIX</span>}
        <span className="fintheon-header-vix-chip__value text-xs font-mono tabular-nums">
          <FintheonNumericText
            text={String(level == null ? "--" : level.toFixed(2))}
          />
        </span>
      </div>
    </div>
  );
}

function vixRegime(level: number | null) {
  if (level == null) return "unknown";
  if (level >= 22) return "severe";
  if (level >= 16) return "elevated";
  if (level >= 14) return "caution";
  return "calm";
}
